import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useAnchoredPopover } from '../hooks/useAnchoredPopover';

export interface Av2SelectOption {
    value: string;
    label: React.ReactNode;
}

interface Props {
    options: Av2SelectOption[];
    value: string;
    onChange: (next: string) => void;
    ariaLabel?: string;
}

/**
 * Minimal custom dropdown matching the V2 design language: pill trigger,
 * floating panel with soft shadow + fade-up animation, clean hover rows,
 * subtle accent dot for the selected option (no garish blue highlight bar).
 * No search input — built for short option lists (3–6 items).
 *
 * Click outside or ESC to close; arrow keys + Enter for keyboard nav.
 */
const Av2Select: React.FC<Props> = ({ options, value, onChange, ariaLabel }) => {
    const [open, setOpen] = useState(false);
    const [focusIdx, setFocusIdx] = useState(0);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const selected = options.find((o) => o.value === value) ?? options[0];

    // Reset focus to the active item when opening
    useEffect(() => {
        if (open) {
            const idx = options.findIndex((o) => o.value === value);
            setFocusIdx(idx >= 0 ? idx : 0);
        }
    }, [open, options, value]);

    // Panel is portaled to the .av2-page root (escapes the toolbar's stacking
    // context, keeps the --av2-* token scope); the hook owns positioning +
    // click-outside + Escape.
    const { panelRef, style: panelStyle, portalTarget } = useAnchoredPopover(
        triggerRef,
        open,
        () => setOpen(false),
        'end',
        true, // panel CSS uses min-width:100% — pin it to the trigger width
    );

    // Keyboard: arrows + enter + escape
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setOpen(false);
            triggerRef.current?.focus();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) {
                setOpen(true);
            } else {
                setFocusIdx((i) => (i + 1) % options.length);
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (open) setFocusIdx((i) => (i - 1 + options.length) % options.length);
        } else if (e.key === 'Enter' || e.key === ' ') {
            if (open) {
                e.preventDefault();
                onChange(options[focusIdx].value);
                setOpen(false);
                triggerRef.current?.focus();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                setOpen(true);
            }
        }
    };

    return (
        <div className="av2-sel" onKeyDown={onKeyDown}>
            <button
                type="button"
                ref={triggerRef}
                className={`av2-sel__trigger${open ? ' av2-sel__trigger--open' : ''}`}
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
            >
                <span className="av2-sel__value">{selected.label}</span>
                <svg
                    className="av2-sel__chevron"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && portalTarget && ReactDOM.createPortal(
                <div ref={panelRef} style={panelStyle} className="av2-sel__panel" role="listbox" aria-activedescendant={`av2-sel-opt-${focusIdx}`}>
                    {options.map((opt, i) => {
                        const isSelected = opt.value === value;
                        const isFocused = i === focusIdx;
                        return (
                            <button
                                key={opt.value}
                                id={`av2-sel-opt-${i}`}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                className={`av2-sel__option${
                                    isSelected ? ' av2-sel__option--selected' : ''
                                }${isFocused ? ' av2-sel__option--focused' : ''}`}
                                onClick={() => {
                                    onChange(opt.value);
                                    setOpen(false);
                                    triggerRef.current?.focus();
                                }}
                                onMouseEnter={() => setFocusIdx(i)}
                            >
                                <span className="av2-sel__option-dot" aria-hidden="true" />
                                <span className="av2-sel__option-label">{opt.label}</span>
                            </button>
                        );
                    })}
                </div>,
                portalTarget
            )}
        </div>
    );
};

export default Av2Select;
